// 2026-05-26 - FormuLearning - 프로젝트 정보 모달 (MOD-PROJECT-INFO)
// 2026-09-21 수정 - 이슈 #77(G6): **이름·Description 수정**(Edit → Save/Cancel · M901-02·06, REQ-UX-32).
//   API Name → Description(memo) · Location(폴더 경로) · Last Updated → **Last Modified**(내부데이터
//   최종 수정일, BR-PRJ-03 — 이름을 고쳐도 바뀌지 않는다).
// 2026-09-23 수정 - 이슈 #82: 진행 중 작업이 있으면 Hide·Delete 를 막고 안내한다 — 작업 대상 프로젝트를 치우는 조작이다.
//   이름·Description 편집은 전환이 아니므로 열어 둔다.
// 2026-09-26 수정 - 이슈 #95 단계 3-5: 스텝 명칭 → `lib/steps`.
// 2026-11-03 수정 - 이슈 #114: viewer 는 Hide/Unhide·Delete·Edit 비활성.
// 파일 위치: frontend/src/features/project/ProjectInfoModal.tsx

import { useState } from 'react'
import Modal from '../../components/feedback/Modal'
import { useProjectStore } from '../../stores/projectStore'
import { useUiStore } from '../../stores/uiStore'
import { toast } from '../../stores/toastStore'
import { blockIfBusy } from '../../stores/busyStore'
import { apiErrorMessage } from '../../lib/apiError'
import { folderPath } from './folderTree'
import { STEP_LABELS } from '../../lib/steps'
import { useCanWrite, READ_ONLY_TITLE } from '../../hooks/useCanWrite'


export default function ProjectInfoModal() {
  const { activeModal, closeModal } = useUiStore()
  const project = useProjectStore((s) => s.currentProject)
  const setHidden = useProjectStore((s) => s.setHidden)
  const canWrite = useCanWrite()
  const removeProject = useProjectStore((s) => s.removeProject)
  const updateProjectInfo = useProjectStore((s) => s.updateProjectInfo)
  const folders = useProjectStore((s) => s.folders)
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [saving, setSaving] = useState(false)

  // 열릴 때마다 보기 모드로 시작한다 — 닫았다 다시 열면 편집 중이던 값이 남지 않게.
  // (렌더 중 이전 값과 비교해 조정 — effect 안 setState 는 연쇄 렌더를 부른다)
  const open = activeModal === 'project-info'
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) setEditing(false)
  }

  if (!project) return null

  const startEdit = () => {
    setName(project.name)
    setDescription(project.memo ?? '')
    setEditing(true)
  }

  const handleSave = async () => {
    if (!name.trim()) return
    setSaving(true)
    try {
      await updateProjectInfo(project.id, { name: name.trim(), memo: description.trim() })
      setEditing(false)
      toast('프로젝트 정보를 저장했습니다.')
    } catch (err) {
      toast(apiErrorMessage(err, '프로젝트 정보를 저장하지 못했습니다.'), 'warn')
    } finally {
      setSaving(false)
    }
  }

  const location = folderPath(new Map(folders.map((f) => [f.id, f])), project.folder_id)
  const inputCls = 'w-full rounded-md border bg-bg-input px-2 py-1.5 text-xs text-text-body outline-none'

  // 2026-07-09 - 소프트 삭제 (이슈 #35): 실수 방지를 위해 먼저 Hide 한 뒤 Unhide 옆에서만 삭제.
  const handleDelete = async () => {
    if (blockIfBusy()) return
    if (!window.confirm('Are you sure you want to delete this project?')) return
    await removeProject(project.id)
    closeModal()
  }

  const stepLabel = project.current_step >= 1 && project.current_step <= 6
    ? STEP_LABELS[project.current_step - 1]
    : '—'

  return (
    <Modal open={open} onClose={closeModal} title="Project Information">
      <div className="space-y-3">
        <div className="rounded-md border p-3" style={{ borderColor: 'var(--border-subtle)' }}>
          <div className="grid grid-cols-2 gap-y-2.5 text-[11px]">
            <div className="col-span-2">
              <span className="text-text-muted">Project Name</span>
              {editing ? (
                <input
                  aria-label="Project Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={200}
                  autoFocus
                  className={inputCls}
                  style={{ borderColor: 'var(--border-soft)' }}
                />
              ) : (
                <p className="font-semibold text-text-primary">{project.name}</p>
              )}
            </div>
            <div className="col-span-2">
              <span className="text-text-muted">Description</span>
              {editing ? (
                <textarea
                  aria-label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className={`${inputCls} resize-y`}
                  style={{ borderColor: 'var(--border-soft)' }}
                />
              ) : (
                <p className="whitespace-pre-wrap break-words font-medium text-text-body">{project.memo || '—'}</p>
              )}
            </div>
            <div className="col-span-2">
              <span className="text-text-muted">Location</span>
              <p className="font-medium text-text-body">{location ? `📁 ${location}` : 'My Projects (root)'}</p>
            </div>
            <div>
              <span className="text-text-muted">Current Step</span>
              <p className="font-medium text-text-body">Step {project.current_step} · {stepLabel}</p>
            </div>
            <div>
              <span className="text-text-muted">Status</span>
              <p className="font-medium" style={{ color: project.status === 'completed' ? 'var(--color-accent-teal)' : 'var(--color-accent-purple)' }}>
                {project.status === 'completed' ? 'COMPLETED' : 'IN PROGRESS'}
              </p>
            </div>
            {project.smiles && (
              <div className="col-span-2">
                <span className="text-text-muted">SMILES</span>
                <p className="break-all font-mono text-[10px] text-text-body">{project.smiles}</p>
              </div>
            )}
            <div>
              <span className="text-text-muted">Created</span>
              <p className="text-text-body">{new Date(project.created_at).toLocaleString('ko-KR')}</p>
            </div>
            <div>
              <span className="text-text-muted" title="Last change to step data (not name or folder)">Last Modified</span>
              <p className="text-text-body">{new Date(project.data_updated_at).toLocaleString('ko-KR')}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between pt-1">
          <div className="flex items-center gap-2">
            {/* 2026-06-19 - 소프트 숨김/해제 (이슈 #7) */}
            <button
              onClick={() => blockIfBusy() || setHidden(project.id, !project.is_hidden)}
              disabled={!canWrite}
              title={canWrite ? undefined : READ_ONLY_TITLE}
              className="rounded-md px-4 py-2 text-xs font-medium text-text-secondary disabled:opacity-40 transition-colors hover:bg-bg-input"
            >
              {project.is_hidden ? '🙈 Unhide' : '🙈 Hide'}
            </button>
            {/* 2026-07-09 - 소프트 삭제 (이슈 #35): 숨긴 상태에서만 노출(2단계 안전장치) */}
            {project.is_hidden && (
              <button
                onClick={handleDelete}
                disabled={!canWrite}
                title={canWrite ? undefined : READ_ONLY_TITLE}
                className="rounded-md px-4 py-2 text-xs font-medium text-red-400 disabled:opacity-40 transition-colors hover:bg-red-500/10"
              >
                🗑 Delete
              </button>
            )}
          </div>
          {editing ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="rounded-md px-4 py-2 text-xs text-text-secondary transition-colors hover:bg-bg-input"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !name.trim()}
                className="rounded-md bg-accent-purple px-4 py-2 text-xs font-semibold text-[#1a1b1e] transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={startEdit}
                disabled={!canWrite}
                title={canWrite ? undefined : READ_ONLY_TITLE}
                className="rounded-md px-4 py-2 text-xs font-medium text-text-secondary disabled:opacity-40 transition-colors hover:bg-bg-input"
              >
                ✎ Edit
              </button>
              <button
                onClick={closeModal}
                className="rounded-md bg-accent-teal px-4 py-2 text-xs font-semibold text-[#1a1b1e] transition-opacity hover:opacity-90"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}
